import axiosInstance from './AxiosInstance';
import swal from "sweetalert";

/**
 * Decodifica un JWT sin dependencias externas.
 * Devuelve el payload como objeto JavaScript.
 */
function decodeJwt(token) {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        console.warn('No se pudo decodificar el JWT', e);
        return {};
    }
}

export function signUp(email, password, nombre, apellido, tipoPerfil, razonSocial = null) {
    const postData = {
        email,
        password,
        nombre,
        apellido,
        tipoPerfil, // "USUARIO" o "EMPRESA"
    };

    // Si es una empresa, agregar la razón social
    if (tipoPerfil === 'EMPRESA' && razonSocial) {
        postData.razonSocial = razonSocial;
    }

    return axiosInstance.post('/auth/register', postData);
}

export function login(email, password) {
    const postData = {
        email,
        password,
    };
    return axiosInstance.post('/auth/login', postData);
}

/**
 * Obtiene el perfil completo del usuario autenticado.
 * El token se envía automáticamente vía AxiosInstance si está configurado el interceptor.
 */
export function getUserProfile() {
    return axiosInstance.get('/user/profile');
}

/**
 * Cambia la contraseña del usuario autenticado.
 * POST /auth/change-password
 * @param {Object} passwordData - Objeto con oldPassword, newPassword y confirmPassword
 */
export function changePassword(passwordData) {
    const postData = {
        passwordActual: passwordData.oldPassword,
        passwordNueva: passwordData.newPassword,
    };
    return axiosInstance.post('/auth/change-password', postData);
}


const ERROR_MESSAGES = {
    'Credenciales inválidas': {
        title: 'Error',
        message: 'El correo o la contraseña no son correctos',
        button: 'Intenta de nuevo'
    },

    'EMAIL_EXISTS': {
        title: 'Error',
        message: 'Este correo electrónico ya está registrado',
        button: 'Intentar con otro correo'
    },

    'USER_NOT_FOUND': {
        title: 'Error',
        message: 'Usuario no encontrado',
        button: 'Intenta de nuevo'
    },

};

export function formatError(errorResponse) {
    const message = errorResponse?.message || errorResponse?.error?.message;
    const statusCode = errorResponse?.statusCode;

    let errorConfig = ERROR_MESSAGES[message];

    // Si es un error 401 y no se encuentra en el mapeo, usar el de credenciales
    if (!errorConfig && statusCode === 401) {
        errorConfig = ERROR_MESSAGES['Credenciales inválidas'];
    }

    if (errorConfig) {
        swal(errorConfig.title, errorConfig.message, "error", { button: errorConfig.button });
        return message;
    }

    // Verificar si es un mensaje de cuenta rechazada y agregar contacto
    let finalMessage = message || "Ha ocurrido un error. Por favor, intenta de nuevo";
    const messageLowercase = finalMessage.toLowerCase();
    
    if (messageLowercase.includes('rechazada') || messageLowercase.includes('rechazado')) {
        finalMessage += '\n\nPara más información, contacta a uniempleo@unicaribe.edu.do';
    }

    swal("Error", finalMessage, "error", { button: "Intenta de nuevo" });
    return message || 'Error desconocido';
}

export function saveTokenInLocalStorage(tokenDetails) {
    if (tokenDetails.access_token) {
        const payload = decodeJwt(tokenDetails.access_token);

        if (payload && payload.exp) {
            const expireMs = payload.exp * 1000;
            tokenDetails.expireDate = new Date(expireMs);
            console.log("🔵 expireDate calculado desde JWT:", tokenDetails.expireDate);
        }
    }

    if (!tokenDetails.expireDate && tokenDetails.expiresIn) {
        tokenDetails.expireDate = new Date(
            new Date().getTime() + tokenDetails.expiresIn * 1000,
        );
    }

    const dataToStore = {
        access_token: tokenDetails.access_token,
        expireDate: tokenDetails.expireDate,

        user: {
            id: tokenDetails.user?.id || null,
            email: tokenDetails.user?.email || null,
            nombre: tokenDetails.user?.nombre || null,
            apellido: tokenDetails.user?.apellido || null,
            fechaRegistro: tokenDetails.user?.fechaRegistro || null,
            tipoPerfil: tokenDetails.user?.tipoPerfil || null,
            perfilUsuarioId: tokenDetails.user?.perfilUsuarioId || null,
            perfilEmpresaId: tokenDetails.user?.perfilEmpresaId || null,
            createdAt: tokenDetails.user?.createdAt || null,
            updatedAt: tokenDetails.user?.updatedAt || null,
            perfilUsuario: tokenDetails.user?.perfilUsuario || null,
            perfilEmpresa: tokenDetails.user?.perfilEmpresa || null,
        },
    };

    try {
        const jsonString = JSON.stringify(dataToStore);
        localStorage.setItem('userDetails', jsonString);

        // Verificar que realmente se guardó
        const verificacion = localStorage.getItem('userDetails');
        console.log("✅ Verificación - localStorage guardado:", verificacion ? JSON.parse(verificacion) : "NULL - NO SE GUARDÓ");
    } catch (error) {
        console.error("❌ ERROR al guardar en localStorage:", error);
        console.error("❌ Error name:", error.name);
        console.error("❌ Error message:", error.message);
    }
}


export function runLogoutTimer(getState, timer, history) {
    setTimeout(() => {
        const { logout } = getState();
        logout(history);
    }, timer);
}

export async function checkAutoLogin(getState, history) {
    console.log('🔍 Verificando auto-login...');
    const tokenDetailsString = localStorage.getItem('userDetails');

    if (!tokenDetailsString) {
        console.log('❌ No hay token en localStorage');
        const { logout, setAuthReady } = getState();
        logout(history, false);
        setAuthReady(true);
        return;
    }

    let tokenDetails;
    try {
        tokenDetails = JSON.parse(tokenDetailsString);
    } catch (error) {
        console.error('❌ Error al parsear token de localStorage:', error);
        localStorage.removeItem('userDetails');
        const { logout, setAuthReady } = getState();
        logout(history, false);
        setAuthReady(true);
        return;
    }

    // Verificar expiración local del token
    let expireDate = new Date(tokenDetails.expireDate);
    let todaysDate = new Date();

    if (todaysDate > expireDate) {
        console.log('❌ Token expirado localmente');
        localStorage.removeItem('userDetails');
        const { logout, setAuthReady } = getState();
        logout(history, false);
        setAuthReady(true);
        return;
    }

    // Validar token contra el backend
    try {
        console.log('🔄 Validando token con el backend...');
        const response = await getUserProfile();

        console.log('✅ Token válido - Perfil obtenido del backend');

        // Actualizar tokenDetails con los datos más recientes del perfil
        const updatedTokenDetails = {
            ...tokenDetails,
            user: response.data
        };

        // Actualizar localStorage con los datos más recientes
        localStorage.setItem('userDetails', JSON.stringify(updatedTokenDetails));

        const { loginConfirmed } = getState();
        loginConfirmed(updatedTokenDetails);

        const timer = expireDate.getTime() - todaysDate.getTime();
        runLogoutTimer(getState, timer, history);

        console.log('✅ Auto-login completado exitosamente');
    } catch (error) {
        console.error('❌ Error al validar token con el backend:', error);
        console.log('⚠️ Token inválido o rechazado por el servidor - Cerrando sesión');

        // Si el backend rechaza el token (ej. 401), limpiar la sesión
        localStorage.removeItem('userDetails');
        const { logout, setAuthReady } = getState();
        logout(history, false);
        setAuthReady(true);
    }
}


/**
 * Solicita el envío de un email para restablecer la contraseña.
 * POST /auth/forgot-password
 * @param {string} email
 */
export function forgotPassword(email) {
    return axiosInstance.post('/auth/forgot-password', { email });
}

/**
 * Restablece la contraseña usando el token recibido por email.
 * POST /auth/reset-password
 * @param {string} token - Token obtenido del query param de la URL
 * @param {string} passwordNueva - Nueva contraseña (mínimo 6 caracteres)
 */
export function resetPassword(token, passwordNueva) {
    return axiosInstance.post('/auth/reset-password', { token, passwordNueva });
}
